import { createUser, deleteUser, fetchUsers, updateUser } from "@/api/user";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import type { Reactive } from "vue";
import { computed, ref, watch } from "vue";

interface UserFilters {
  keyword: string;
  page: number;
  size: number;
  status: boolean[];
}

type UserQueryKey = ["users", UserFilters];

const fetchUsersQuery = async ({ queryKey }: { queryKey: UserQueryKey }) => {
  const [, filters] = queryKey;
  return fetchUsers(
    filters.keyword,
    filters.page,
    filters.size,
    filters.status,
  );
};

export function useUsersQuery(filters: Reactive<UserFilters>) {
  const queryKey = ref<UserQueryKey>(["users", filters]);

  watch(
    filters,
    (newFilters) => {
      queryKey.value = ["users", newFilters];
    },
    { deep: true, immediate: true },
  );

  const queryResult = useQuery({
    queryKey: queryKey,
    queryFn: fetchUsersQuery,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const users = computed(() => {
    return queryResult.data.value?.data.data || [];
  });

  const totalFiltered = computed(() => {
    return queryResult.data.value?.data.total_filter || 0;
  });

  return {
    ...queryResult,
    users,
    totalFiltered,
  };
}

export function useCreateUserMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { username: string; password: string; role: string; avatar_seed?: string }) =>
      createUser(data.username, data.password, data.role, data.avatar_seed),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

export function useUpdateUserMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      id: string;
      username?: string;
      password?: string;
      enable: boolean;
      role?: string;
      avatar_seed?: string;
    }) =>
      updateUser(
        data.id,
        data.username,
        data.password || undefined,
        data.enable,
        data.role,
        data.avatar_seed,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

export function useDeleteUserMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
}
